import { useEffect, lazy, Suspense, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { RoleRoute } from "@/components/RoleRoute";
import ScrollToTop from "@/components/ScrollToTop";
import { AdminPWAManifest } from "@/components/admin/AdminPWAManifest";
import { AdminSectionErrorBoundary } from "@/components/admin/AdminSectionErrorBoundary";
import PrivacyMeta from "@/components/PrivacyMeta";
import { useGAPageviews } from "@/hooks/useGAPageviews";
import { trackPhoneCall } from "@/lib/trackEvent";

const AIChatWidget = lazy(() =>
  import("@/components/AIChatWidget").then((module) => ({ default: module.AIChatWidget })),
);

function PublicChatWidget() {
  const { pathname } = useLocation();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (pathname.startsWith("/admin") || pathname.startsWith("/portal")) return;
    const win = window as Window & {
      requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
      cancelIdleCallback?: (handle: number) => void;
    };
    const fallback = window.setTimeout(() => setReady(true), 4000);
    const idle = win.requestIdleCallback?.(() => {
      window.clearTimeout(fallback);
      setReady(true);
    }, { timeout: 4000 });

    return () => {
      window.clearTimeout(fallback);
      if (idle !== undefined) win.cancelIdleCallback?.(idle);
    };
  }, [pathname]);

  if (pathname.startsWith("/admin") || pathname.startsWith("/portal")) return null;
  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <AIChatWidget />
    </Suspense>
  );
}

function GAPageviewTracker() {
  useGAPageviews();
  return null;
}

/**
 * Redirect any non-root URL ending in "/" to the no-slash variant so
 * we never publish duplicate URLs (e.g. /contact/ → /contact).
 */
function TrailingSlashRedirect() {
  const { pathname, search, hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (pathname !== "/" && pathname.endsWith("/")) {
      navigate(pathname.replace(/\/+$/, "") + search + hash, { replace: true });
    }
  }, [pathname, search, hash, navigate]);
  return null;
}

/**
 * Re-runs the Valentine Digital dynamic optimization loader on every
 * client-side navigation so route-level SEO (title, description,
 * canonical, OG, Twitter, JSON-LD) is refreshed by the external
 * editor after each SPA route change.
 */
function ExternalSeoRefresher() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    // Skip re-injection entirely on 404 routes — NotFound.tsx owns the head there.
    if (document.documentElement.dataset.routeStatus === "404") return;

    const w = window as any;

    // If SearchAtlas/OTTO provides an explicit refresh hook, invoke it safely.
    if (typeof w.saDyoRefresh === "function") {
      try { w.saDyoRefresh(); return; } catch { /* ignore */ }
    }
    if (typeof w.saDyoInit === "function") {
      try { w.saDyoInit(); return; } catch { /* ignore */ }
    }

    // If dynamic_optimization script has already been loaded into global scope,
    // re-evaluating top-level `let saDyoHeaderInsertionCount` causes an uncaught SyntaxError.
    if (w.saDyoHeaderInsertionCount !== undefined || document.getElementById("sa-dynamic-optimization-loader")) {
      return;
    }

    const existingBoot = document.getElementById("sa-dynamic-optimization");
    if (!existingBoot) return;
    const clone = document.createElement("script");
    for (const attr of Array.from(existingBoot.attributes)) {
      clone.setAttribute(attr.name, attr.value);
    }
    existingBoot.parentNode?.replaceChild(clone, existingBoot);
  }, [pathname, search]);
  return null;
}

/**
 * Global tel: click listener — tracks every phone-link click as a Google Ads
 * conversion regardless of which component rendered the link. Avoids having
 * to add onClick handlers to dozens of <a href="tel:..."> elements.
 */
function GlobalPhoneClickTracker() {
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.("a[href^='tel:']") as HTMLAnchorElement | null;
      if (!anchor) return;
      // Best-effort location label: data-cta-location → nearest section id → pathname
      const location =
        anchor.dataset.ctaLocation ||
        anchor.closest("[id]")?.id ||
        window.location.pathname;
      trackPhoneCall(location);
    };
    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true } as any);
  }, []);
  return null;
}
import NotFound from "./pages/NotFound";

// Lazy-loaded programmatic service×city pages
const ServiceCityPageRoute = lazy(() => import("./components/ServiceCityPageRoute"));

// Q1 cluster + county hubs (lazy-loaded)
const MaintenancePlansHub = lazy(() => import("./pages/MaintenancePlansHub"));
const PaidServicesHub = lazy(() => import("./pages/PaidServicesHub"));
const IndustriesHub = lazy(() => import("./pages/IndustriesHub"));
const DoorsHub = lazy(() => import("./pages/DoorsHub"));
const FloorsConcreteHub = lazy(() => import("./pages/FloorsConcreteHub"));
const HuronCountyHub = lazy(() => import("./pages/HuronCountyHub"));
const TuscolaCountyHub = lazy(() => import("./pages/TuscolaCountyHub"));
const SanilacCountyHub = lazy(() => import("./pages/SanilacCountyHub"));

// Q2 proof authority pages
const ProjectsHub = lazy(() => import("./pages/ProjectsHub"));
const ProjectDetail = lazy(() => import("./pages/ProjectDetail"));
const ProjectCollection = lazy(() => import("./pages/ProjectCollection"));
const CostGuideDetail = lazy(() => import("./pages/CostGuideDetail"));
const CostGuideCity = lazy(() => import("./pages/CostGuideCity"));
const KnowledgeHub = lazy(() => import("./pages/KnowledgeHub"));
const KnowledgeArticle = lazy(() => import("./pages/KnowledgeArticle"));
const Credentials = lazy(() => import("./pages/Credentials"));
const WarrantyPage = lazy(() => import("./pages/Warranty"));
const Team = lazy(() => import("./pages/Team"));
const FounderProfile = lazy(() => import("./pages/FounderProfile"));
const ProductsHub = lazy(() => import("./pages/ProductsHub"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const ProductComparison = lazy(() => import("./pages/ProductComparison"));

// Q3 cost guides + comparisons
const CostGuidesHub = lazy(() => import("./pages/CostGuidesHub"));
const CompareHub = lazy(() => import("./pages/CompareHub"));

// Q4 video, press, glossary, commercial RFP
const VideosHub = lazy(() => import("./pages/VideosHub"));
const PressHub = lazy(() => import("./pages/PressHub"));
const Glossary = lazy(() => import("./pages/Glossary"));
const CommercialRFP = lazy(() => import("./pages/CommercialRFP"));

// Wave 2 — diagnostics + FAQ center
const DiagnosticsHub = lazy(() => import("./pages/DiagnosticsHub"));
const DiagnosticDetail = lazy(() => import("./pages/DiagnosticDetail"));
const FaqCenter = lazy(() => import("./pages/FaqCenter"));

// Wave 3 — resources hub + pillar guides
const ResourcesHub = lazy(() => import("./pages/ResourcesHub"));
const ResourceDetail = lazy(() => import("./pages/ResourceDetail"));
const QuestionsHub = lazy(() => import("./pages/QuestionsHub"));

// Wave 5 — authority dashboard
const AdminAuthorityDashboard = lazy(() => import("./pages/admin/AuthorityDashboard"));

// Lazy-loaded homepage (keeps framer-motion + gallery images out of main bundle)
const Index = lazy(() => import("./pages/Index"));

// Lazy-loaded service pages
const InteriorPainting = lazy(() => import("./pages/InteriorPainting"));
const ExteriorPainting = lazy(() => import("./pages/ExteriorPainting"));
const BarnPainting = lazy(() => import("./pages/BarnPainting"));
const DeckStaining = lazy(() => import("./pages/DeckStaining"));
const CabinetPainting = lazy(() => import("./pages/CabinetPainting"));
const CommercialPainting = lazy(() => import("./pages/CommercialPainting"));
const FencePainting = lazy(() => import("./pages/FencePainting"));
const CabinStaining = lazy(() => import("./pages/CabinStaining"));
const CabinRestoration = lazy(() => import("./pages/CabinRestoration"));
const CabinPainting = lazy(() => import("./pages/CabinPainting"));

// Lazy-loaded main pages
const About = lazy(() => import("./pages/About"));
const Gallery = lazy(() => import("./pages/Gallery"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const Contact = lazy(() => import("./pages/Contact"));
const Community = lazy(() => import("./pages/Community"));
const Reviews = lazy(() => import("./pages/Reviews"));
const ServiceQualityTrust = lazy(() => import("./pages/ServiceQualityTrust"));
const QRCodePage = lazy(() => import("./pages/QRCode"));
const BestPaintersThumbRegion2026 = lazy(() => import("./pages/BestPaintersThumbRegion2026"));
const PaintingCostGuide = lazy(() => import("./pages/PaintingCostGuide"));
const ShakeRoofRestoration = lazy(() => import("./pages/ShakeRoofRestoration"));
const WoodRestoration = lazy(() => import("./pages/WoodRestoration"));
const CedarSidingRestoration = lazy(() => import("./pages/CedarSidingRestoration"));
const DockRestoration = lazy(() => import("./pages/DockRestoration"));
const PergolaGazeboRestoration = lazy(() => import("./pages/PergolaGazeboRestoration"));
const WindowDoorFrameRestoration = lazy(() => import("./pages/WindowDoorFrameRestoration"));
const SoffitFasciaRestoration = lazy(() => import("./pages/SoffitFasciaRestoration"));
const InteriorWoodPanelingRestoration = lazy(() => import("./pages/InteriorWoodPanelingRestoration"));

// Winter interior + competitor-gap services
const DrywallRepair = lazy(() => import("./pages/DrywallRepair"));
const DrywallFinishing = lazy(() => import("./pages/DrywallFinishing"));
const CeilingPainting = lazy(() => import("./pages/CeilingPainting"));
const TrimPainting = lazy(() => import("./pages/TrimPainting"));
const PressureWashing = lazy(() => import("./pages/PressureWashing"));
const LightCarpentryPrep = lazy(() => import("./pages/LightCarpentryPrep"));

// Marine Coatings (hub + 9 spokes)
const MarineCoatings = lazy(() => import("./pages/MarineCoatings"));
const BoatHullPainting = lazy(() => import("./pages/BoatHullPainting"));
const DockSteelCoatings = lazy(() => import("./pages/DockSteelCoatings"));
const BoathouseCoatings = lazy(() => import("./pages/BoathouseCoatings"));
const SeawallCoatings = lazy(() => import("./pages/SeawallCoatings"));
const PontoonAluminumRefinishing = lazy(() => import("./pages/PontoonAluminumRefinishing"));
const MarinaInfrastructureCoatings = lazy(() => import("./pages/MarinaInfrastructureCoatings"));
const LighthouseHistoricMarineRestoration = lazy(() => import("./pages/LighthouseHistoricMarineRestoration"));
const BoatTrailerDavitCoatings = lazy(() => import("./pages/BoatTrailerDavitCoatings"));
const SplashZoneEpoxyCoatings = lazy(() => import("./pages/SplashZoneEpoxyCoatings"));

// Industrial Coatings (hub + 13 spokes)
const IndustrialCoatings = lazy(() => import("./pages/IndustrialCoatings"));
const EpoxyFloorCoatings = lazy(() => import("./pages/EpoxyFloorCoatings"));
const PolyasparticFloorCoatings = lazy(() => import("./pages/PolyasparticFloorCoatings"));
const GrainBinSiloCoatings = lazy(() => import("./pages/GrainBinSiloCoatings"));
const StructuralSteelPainting = lazy(() => import("./pages/StructuralSteelPainting"));
const TankVesselLining = lazy(() => import("./pages/TankVesselLining"));
const IndustrialWallCeilingCoatings = lazy(() => import("./pages/IndustrialWallCeilingCoatings"));
const ConcreteSealersPolished = lazy(() => import("./pages/ConcreteSealersPolished"));
const WarehouseLineStriping = lazy(() => import("./pages/WarehouseLineStriping"));
const IntumescentFireproofCoatings = lazy(() => import("./pages/IntumescentFireproofCoatings"));
const ChemicalResistantCoatings = lazy(() => import("./pages/ChemicalResistantCoatings"));
const PowderCoatTouchupFieldRepair = lazy(() => import("./pages/PowderCoatTouchupFieldRepair"));
const AntiGraffitiAntiMicrobialCoatings = lazy(() => import("./pages/AntiGraffitiAntiMicrobialCoatings"));

// Door Refinishing (hub + 4 leaves)
const DoorRefinishing = lazy(() => import("./pages/DoorRefinishing"));
const FrontDoorRefinishing = lazy(() => import("./pages/FrontDoorRefinishing"));
const EntryDoorStaining = lazy(() => import("./pages/EntryDoorStaining"));
const FiberglassDoorRestoration = lazy(() => import("./pages/FiberglassDoorRestoration"));
const DoorColorConsulting = lazy(() => import("./pages/DoorColorConsulting"));

// Log Home Restoration (hub + 6 leaves)
const LogHome = lazy(() => import("./pages/LogHome"));
const LogHomeChinking = lazy(() => import("./pages/LogHomeChinking"));
const LogHomeBorateTreatment = lazy(() => import("./pages/LogHomeBorateTreatment"));
const LogHomeMediaBlasting = lazy(() => import("./pages/LogHomeMediaBlasting"));
const LogHomeSuperdeckStaining = lazy(() => import("./pages/LogHomeSuperdeckStaining"));
const LogHomeInspection = lazy(() => import("./pages/LogHomeInspection"));
const LogHomeMaintenancePlan = lazy(() => import("./pages/LogHomeMaintenancePlan"));

// Agricultural Coatings (hub + 5 leaves)
const AgriculturalCoatings = lazy(() => import("./pages/AgriculturalCoatings"));
const PoleBarnRepaint = lazy(() => import("./pages/PoleBarnRepaint"));
const SiloCoating = lazy(() => import("./pages/SiloCoating"));
const GrainBinRepaint = lazy(() => import("./pages/GrainBinRepaint"));
const DairyBarnSanitaryCoating = lazy(() => import("./pages/DairyBarnSanitaryCoating"));
const EquipmentShedCoating = lazy(() => import("./pages/EquipmentShedCoating"));

// Residential Specialty (10 leaves)
const BrickLimewashing = lazy(() => import("./pages/BrickLimewashing"));
const GermanSmearMortarWash = lazy(() => import("./pages/GermanSmearMortarWash"));
const CedarShakeRestoration = lazy(() => import("./pages/CedarShakeRestoration"));
const DeckResurfacing = lazy(() => import("./pages/DeckResurfacing"));
const ConcreteStaining = lazy(() => import("./pages/ConcreteStaining"));
const EpoxyGarageFloors = lazy(() => import("./pages/EpoxyGarageFloors"));
const HistoricPaintRestoration = lazy(() => import("./pages/HistoricPaintRestoration"));
const CabinetRefinishing = lazy(() => import("./pages/CabinetRefinishing"));
const GarageDoorRefinishing = lazy(() => import("./pages/GarageDoorRefinishing"));
const ShutterRestoration = lazy(() => import("./pages/ShutterRestoration"));

// Marine & Industrial (Sprint 6 — 7 leaves)
const MarineBottomPaint = lazy(() => import("./pages/MarineBottomPaint"));
const DockRefinishing = lazy(() => import("./pages/DockRefinishing"));
const BoatLiftCoating = lazy(() => import("./pages/BoatLiftCoating"));
const IndustrialTankCoating = lazy(() => import("./pages/IndustrialTankCoating"));
const AntiGraffitiCoating = lazy(() => import("./pages/AntiGraffitiCoating"));
const IntumescentFireproofing = lazy(() => import("./pages/IntumescentFireproofing"));
const ElastomericRoofCoating = lazy(() => import("./pages/ElastomericRoofCoating"));

// Industry Verticals (Sprint 7 — 15 leaves)
const HotelInnPainting = lazy(() => import("./pages/HotelInnPainting"));
const HoaPainting = lazy(() => import("./pages/HoaPainting"));
const MarinaPainting = lazy(() => import("./pages/MarinaPainting"));
const WineryOrchardPainting = lazy(() => import("./pages/WineryOrchardPainting"));
const HealthcareFacilityPainting = lazy(() => import("./pages/HealthcareFacilityPainting"));
const SchoolDaycarePainting = lazy(() => import("./pages/SchoolDaycarePainting"));
const MunicipalPainting = lazy(() => import("./pages/MunicipalPainting"));
const SelfStoragePainting = lazy(() => import("./pages/SelfStoragePainting"));
const ApartmentPainting = lazy(() => import("./pages/ApartmentPainting"));
const RestaurantKitchenPainting = lazy(() => import("./pages/RestaurantKitchenPainting"));
const RetailStorefrontPainting = lazy(() => import("./pages/RetailStorefrontPainting"));
const AutoDealershipPainting = lazy(() => import("./pages/AutoDealershipPainting"));
const FuneralHomePainting = lazy(() => import("./pages/FuneralHomePainting"));
const ChurchPainting = lazy(() => import("./pages/ChurchPainting"));
const MuseumHistoricPainting = lazy(() => import("./pages/MuseumHistoricPainting"));

// Moat Paid Services (Sprint 7 — 5)
const PaintFailureDiagnosis = lazy(() => import("./pages/PaintFailureDiagnosis"));
const LogHomeHealthInspection = lazy(() => import("./pages/LogHomeHealthInspection"));
const ColorDesignConsultation = lazy(() => import("./pages/ColorDesignConsultation"));
const HoaSpecWriting = lazy(() => import("./pages/HoaSpecWriting"));
const HistoricPaintAnalysis = lazy(() => import("./pages/HistoricPaintAnalysis"));

// Membership Plans (6 — recurring care subscriptions)
const ResidentialMaintenancePlan = lazy(() => import("./pages/ResidentialMaintenancePlan"));
const LogHomeCarePlan = lazy(() => import("./pages/LogHomeCarePlan"));
const FrontDoorRefreshPlan = lazy(() => import("./pages/FrontDoorRefreshPlan"));
const WaterfrontProtectionPlan = lazy(() => import("./pages/WaterfrontProtectionPlan"));
const FarmCoatingPlan = lazy(() => import("./pages/FarmCoatingPlan"));
const CommercialMaintenancePlan = lazy(() => import("./pages/CommercialMaintenancePlan"));

// Lazy-loaded city pages
const PaintersBadAxeMi = lazy(() => import("./pages/city-pages/PaintersBadAxeMi"));
const PaintersCaroMi = lazy(() => import("./pages/city-pages/PaintersCaroMi"));
const PaintersCassCityMi = lazy(() => import("./pages/city-pages/PaintersCassCityMi"));
const PaintersSanduskyMi = lazy(() => import("./pages/city-pages/PaintersSanduskyMi"));
const PaintersHarborBeachMi = lazy(() => import("./pages/city-pages/PaintersHarborBeachMi"));
const PainersMarletteMi = lazy(() => import("./pages/city-pages/PainersMarletteMi"));
const PaintersSebewaingMi = lazy(() => import("./pages/city-pages/PaintersSebewaingMi"));
const PaintersPigeonMi = lazy(() => import("./pages/city-pages/PaintersPigeonMi"));
const PaintersUblyMi = lazy(() => import("./pages/city-pages/PaintersUblyMi"));
const PaintersPortAustinMi = lazy(() => import("./pages/city-pages/PaintersPortAustinMi"));
const PaintersDeckerville = lazy(() => import("./pages/city-pages/PaintersDeckerville"));
const PaintersCasevilleMi = lazy(() => import("./pages/city-pages/PaintersCasevilleMi"));
const PaintersSandPointMi = lazy(() => import("./pages/city-pages/PaintersSandPointMi"));
const PaintersPortHopeMi = lazy(() => import("./pages/city-pages/PaintersPortHopeMi"));
const PaintersElktonMi = lazy(() => import("./pages/city-pages/PaintersElktonMi"));
const PaintersKindeMi = lazy(() => import("./pages/city-pages/PaintersKindeMi"));
const PaintersOwendaleMi = lazy(() => import("./pages/city-pages/PaintersOwendaleMi"));
const PaintersVassarMi = lazy(() => import("./pages/city-pages/PaintersVassarMi"));
const PaintersReeseMi = lazy(() => import("./pages/city-pages/PaintersReeseMi"));
const PaintersMayvilleMi = lazy(() => import("./pages/city-pages/PaintersMayvilleMi"));
const PaintersMillingtonMi = lazy(() => import("./pages/city-pages/PaintersMillingtonMi"));
const PaintersAkronMi = lazy(() => import("./pages/city-pages/PaintersAkronMi"));
const PaintersUnionvilleMi = lazy(() => import("./pages/city-pages/PaintersUnionvilleMi"));
const PaintersFairgroveMi = lazy(() => import("./pages/city-pages/PaintersFairgroveMi"));
const PaintersKingstonMi = lazy(() => import("./pages/city-pages/PaintersKingstonMi"));
const PaintersCroswellMi = lazy(() => import("./pages/city-pages/PaintersCroswellMi"));
const PaintersFrankenmuthMi = lazy(() => import("./pages/city-pages/PaintersFrankenmuthMi"));
const PaintersBirchRunMi = lazy(() => import("./pages/city-pages/PaintersBirchRunMi"));
const PaintersLexingtonMi = lazy(() => import("./pages/city-pages/PaintersLexingtonMi"));
const PaintersMarineCityMi = lazy(() => import("./pages/city-pages/PaintersMarineCityMi"));
const PaintersPortHuronMi = lazy(() => import("./pages/city-pages/PaintersPortHuronMi"));
const PaintersBayCityMi = lazy(() => import("./pages/city-pages/PaintersBayCityMi"));
const PaintersSaginawMi = lazy(() => import("./pages/city-pages/PaintersSaginawMi"));
const PaintersMidlandMi = lazy(() => import("./pages/city-pages/PaintersMidlandMi"));
const PaintersAlgonacMi = lazy(() => import("./pages/city-pages/PaintersAlgonacMi"));
const PaintersCarsonvilleMi = lazy(() => import("./pages/city-pages/PaintersCarsonvilleMi"));
const PaintersPeckMi = lazy(() => import("./pages/city-pages/PaintersPeckMi"));
const PaintersBrownCityMi = lazy(() => import("./pages/city-pages/PaintersBrownCityMi"));
const PaintersApplegateMi = lazy(() => import("./pages/city-pages/PaintersApplegateMi"));

// Lazy-loaded admin pages
const AdminAuth = lazy(() => import("./pages/admin/Auth"));
const AdminAuthWelcome = lazy(() => import("./pages/admin/AuthWelcome"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminLeads = lazy(() => import("./pages/admin/Leads"));
const AdminLeadWorkspace = lazy(() => import("./pages/admin/LeadWorkspace"));
const AdminIntake = lazy(() => import("./pages/admin/Intake"));
const AdminSaturation = lazy(() => import("./pages/admin/Saturation"));
const AdminCoverageMap = lazy(() => import("./pages/admin/CoverageMap"));
const AdminExpansionRoadmap = lazy(() => import("./pages/admin/ExpansionRoadmap"));
const AdminJobs = lazy(() => import("./pages/admin/Jobs"));
const AdminJobProfitLoss = lazy(() => import("./pages/admin/JobProfitLoss"));
const CrewHome = lazy(() => import("./pages/admin/CrewHome"));
const AdminCrewUpdates = lazy(() => import("./pages/admin/CrewUpdates"));
const AdminCrewAppIssues = lazy(() => import("./pages/admin/CrewAppIssues"));
const AdminEstimates = lazy(() => import("./pages/admin/Estimates"));
const AdminInvoices = lazy(() => import("./pages/admin/Invoices"));
const AdminPayments = lazy(() => import("./pages/admin/Payments"));
const AdminCalendar = lazy(() => import("./pages/admin/Calendar"));
const AdminMediaGallery = lazy(() => import("./pages/admin/MediaGallery"));
const AdminEmailMarketing = lazy(() => import("./pages/admin/EmailMarketing"));
const AdminEmailHealth = lazy(() => import("./pages/admin/EmailHealth"));
const AdminReports = lazy(() => import("./pages/admin/Reports"));
const AdminExecutiveDashboard = lazy(() => import("./pages/admin/ExecutiveDashboard"));
const AdminRevenueRecovery = lazy(() => import("./pages/admin/RevenueRecovery"));
const AdminIntelligenceHub = lazy(() => import("./pages/admin/IntelligenceHub"));
const AdminBusinessBrain = lazy(() => import("./pages/admin/BusinessBrain"));
const AdminEstimatingIntelligence = lazy(() => import("./pages/admin/EstimatingIntelligence"));
const AdminMarketingProfitability = lazy(() => import("./pages/admin/MarketingProfitability"));
const AdminMaintenanceIntelligence = lazy(() => import("./pages/admin/MaintenanceIntelligence"));
const AdminAutonomousOperations = lazy(() => import("./pages/admin/AutonomousOperations"));
const AdminSearchAtlasIntelligence = lazy(() => import("./pages/admin/SearchAtlasIntelligence"));
const AdminContentDomination = lazy(() => import("./pages/admin/ContentDomination"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const QaConsole = lazy(() => import("./pages/admin/QaConsole"));
const AdminTasks = lazy(() => import("./pages/admin/Tasks"));
const AdminCrews = lazy(() => import("./pages/admin/Crews"));
const AdminTeam = lazy(() => import("./pages/admin/Team"));
const AdminSms = lazy(() => import("./pages/admin/Sms"));
const ThankYouEmailJustin = lazy(() => import("./pages/admin/ThankYouEmailJustin"));
const AdminInbox = lazy(() => import("./pages/admin/Inbox"));
const AdminFlyer = lazy(() => import("./pages/admin/Flyer"));
const AdminBlogPosts = lazy(() => import("./pages/admin/BlogPosts"));
const AdminReviews = lazy(() => import("./pages/admin/Reviews"));
const AdminProjects = lazy(() => import("./pages/admin/Projects"));
const AdminProjectEditor = lazy(() => import("./pages/admin/ProjectEditor"));
const AdminHomeFeatured = lazy(() => import("./pages/admin/HomeFeatured"));
const AdminGbpComposer = lazy(() => import("./pages/admin/GbpComposer"));
const AdminAuthorityProgram = lazy(() => import("./pages/admin/AuthorityProgram"));
const AdminContentEditor = lazy(() => import("./pages/admin/ContentEditor"));
const AdminLlmVisibility = lazy(() => import("./pages/admin/LlmVisibility"));
const AdminBlogEngine = lazy(() => import("./pages/admin/BlogEngine"));
const AdminTrophyListicle = lazy(() => import("./pages/admin/TrophyListicle"));
const AdminH2HComparison = lazy(() => import("./pages/admin/H2HComparison"));
const AdminTopicalAuthority = lazy(() => import("./pages/admin/TopicalAuthority"));
const AdminFoundationAuthority = lazy(() => import("./pages/admin/FoundationAuthority"));
const AdminAuthorityAcceleration = lazy(() => import("./pages/admin/AuthorityAcceleration"));
const AdminAutonomousAuthority = lazy(() => import("./pages/admin/AutonomousAuthority"));
const AdminOutreachCampaign = lazy(() => import("./pages/admin/OutreachCampaign"));
const AdminCloudStackTrust = lazy(() => import("./pages/admin/CloudStackTrust"));
const AdminJournalistOutreach = lazy(() => import("./pages/admin/JournalistOutreach"));
const AdminPressReleaseAuthority = lazy(() => import("./pages/admin/PressReleaseAuthority"));
const AdminKeywordPublisherOutreach = lazy(() => import("./pages/admin/KeywordPublisherOutreach"));
const AdminSeoContentStrategy = lazy(() => import("./pages/admin/SeoContentStrategy"));
const AdminEntityGraph = lazy(() => import("./pages/admin/EntityGraph"));
const AdminLocalSeoGeoGrid = lazy(() => import("./pages/admin/LocalSeoGeoGrid"));
const AdminCampaignCockpit = lazy(() => import("./pages/admin/CampaignCockpit"));
const AdminAgentPerformance = lazy(() => import("./pages/admin/AgentPerformance"));
const AdminBlogIdeas = lazy(() => import("./pages/admin/BlogIdeas"));
const AdminTimeClock = lazy(() => import("./pages/admin/TimeClock"));
const AdminTimesheets = lazy(() => import("./pages/admin/Timesheets"));
const AdminExpenses = lazy(() => import("./pages/admin/Expenses"));
const AdminMyPay = lazy(() => import("./pages/admin/MyPay"));
const Certification = lazy(() => import("./pages/Certification"));
const AdminCertifications = lazy(() => import("./pages/admin/Certifications"));
const AdminCertificationApplicant = lazy(() => import("./pages/admin/CertificationApplicant"));
const AdminCertificationQuestions = lazy(() => import("./pages/admin/CertificationQuestions"));
const AdminCertificationPhotos = lazy(() => import("./pages/admin/CertificationPhotos"));

// Lazy-loaded customer portal
const CustomerProposal = lazy(() => import("./pages/portal/CustomerProposal"));
const CustomerEstimate = lazy(() => import("./pages/portal/CustomerEstimate"));
const CustomerInvoice = lazy(() => import("./pages/portal/CustomerInvoice"));
const ChangeOrderPortal = lazy(() => import("./pages/portal/ChangeOrderPortal"));
const SurveyPortal = lazy(() => import("./pages/portal/SurveyPortal"));
const WarrantyClaimPortal = lazy(() => import("./pages/portal/WarrantyClaimPortal"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const ReviewRouter = lazy(() => import("./pages/portal/ReviewRouter"));
const ReviewThanks = lazy(() => import("./pages/portal/ReviewThanks"));
const CustomerFeedback = lazy(() => import("./pages/portal/CustomerFeedback"));
const JobTracker = lazy(() => import("./pages/portal/JobTracker"));
const ReferPortal = lazy(() => import("./pages/portal/Refer"));
const AdminReferrals = lazy(() => import("./pages/admin/Referrals"));
const AdminFollowUps = lazy(() => import("./pages/admin/FollowUps"));
const AdminProfitDashboard = lazy(() => import("./pages/admin/ProfitDashboard"));
const AccountHub = lazy(() => import("./pages/portal/AccountHub"));
const PortalWarranties = lazy(() => import("./pages/portal/Warranties"));
const PortalPhotos = lazy(() => import("./pages/portal/Photos"));
const PortalReferrals = lazy(() => import("./pages/portal/Referrals"));
const CustomerAuth = lazy(() => import("./pages/CustomerAuth"));

// Legal pages
const PrivacyPage = lazy(() => import("./pages/legal/Privacy"));
const ServicesIndex = lazy(() => import("./pages/Services"));
const TermsPage = lazy(() => import("./pages/legal/Terms"));
const SmsConsentPage = lazy(() => import("./pages/legal/SmsConsent"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Always treat data as stale so it refetches when components mount
      staleTime: 0,
      // Refetch fresh data every time the user returns to the tab/app
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      // Keep cache around briefly so navigation feels instant, then refresh
      gcTime: 30_000,
    },
  },
});

// On every cold load of the admin, clear any stale browser caches/storage
// so the user always sees the latest data and code on app open.
if (typeof window !== "undefined") {
  const isAdminPath = window.location.pathname.startsWith("/admin");
  if (isAdminPath) {
    // Remove any leftover service workers from prior PWA experiments
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister().catch(() => {}));
      }).catch(() => {});
    }
    // Wipe Cache Storage entries (HTTP cache controlled by SW/PWAs)
    if ("caches" in window) {
      caches.keys().then((keys) => {
        keys.forEach((k) => caches.delete(k).catch(() => {}));
      }).catch(() => {});
    }
  }

  // Refetch all queries when the standalone PWA / tab becomes visible
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") {
      queryClient.invalidateQueries();
    }
  });
}

function useGoldenSparkles() {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const count = 7;
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
        const distance = 20 + Math.random() * 35;
        const sx = Math.cos(angle) * distance;
        const sy = Math.sin(angle) * distance;
        const size = 4 + Math.random() * 6;
        const isStar = Math.random() > 0.5;

        const el = document.createElement('div');
        el.className = isStar ? 'sparkle-star' : 'sparkle-particle';
        el.style.left = `${e.clientX}px`;
        el.style.top = `${e.clientY}px`;
        el.style.setProperty('--sx', `${sx}px`);
        el.style.setProperty('--sy', `${sy}px`);
        if (isStar) {
          el.style.setProperty('--star-size', `${size + 6}px`);
        } else {
          el.style.width = `${size}px`;
          el.style.height = `${size}px`;
        }
        el.style.animationDelay = `${Math.random() * 0.1}s`;
        document.body.appendChild(el);

        setTimeout(() => el.remove(), 800);
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
}

const App = () => {
  useGoldenSparkles();
  return (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <ImpersonationProvider>
          <ScrollToTop />
          <TrailingSlashRedirect />
          <ExternalSeoRefresher />
          <AdminPWAManifest />
          <Suspense fallback={<div className="min-h-screen" />}>
          <PublicChatWidget />
          <GAPageviewTracker />
          <GlobalPhoneClickTracker />
          <PrivacyMeta />
          <Routes>
            <Route path="/" element={<Index />} />
            
            {/* Service Pages */}
            <Route path="/interior-painting" element={<InteriorPainting />} />

            {/* Q1 Hub Pages */}
            <Route path="/maintenance-plans" element={<MaintenancePlansHub />} />
            <Route path="/paid-services" element={<PaidServicesHub />} />
            <Route path="/industries" element={<IndustriesHub />} />
            <Route path="/doors" element={<DoorsHub />} />
            <Route path="/floors-concrete" element={<FloorsConcreteHub />} />

            {/* Q1 County Hubs */}
            <Route path="/huron-county" element={<HuronCountyHub />} />
            <Route path="/tuscola-county" element={<TuscolaCountyHub />} />
            <Route path="/sanilac-county" element={<SanilacCountyHub />} />

            {/* Q2 Proof Authority */}
            <Route path="/projects" element={<ProjectsHub />} />
            <Route path="/projects/:slug" element={<ProjectDetail />} />
            <Route path="/projects/:facet/:value" element={<ProjectCollection />} />
            <Route path="/credentials" element={<Credentials />} />
            <Route path="/warranty" element={<WarrantyPage />} />
            <Route path="/team" element={<Team />} />
            <Route path="/about/founder" element={<FounderProfile />} />
            <Route path="/about/service-quality-trust" element={<ServiceQualityTrust />} />
            <Route path="/products" element={<ProductsHub />} />
            <Route path="/products/compare/:slug" element={<ProductComparison />} />
            <Route path="/products/:slug" element={<ProductDetail />} />
            <Route path="/cost-guides" element={<CostGuidesHub />} />
            <Route path="/cost-guides/:slug" element={<CostGuideDetail />} />
            <Route path="/cost-guides/:slug/:city" element={<CostGuideCity />} />
            <Route path="/learn" element={<KnowledgeHub />} />
            <Route path="/learn/:cluster" element={<KnowledgeHub />} />
            <Route path="/learn/:cluster/:slug" element={<KnowledgeArticle />} />
            <Route path="/compare" element={<CompareHub />} />
            <Route path="/videos" element={<VideosHub />} />
            <Route path="/press" element={<PressHub />} />
            <Route path="/glossary" element={<Glossary />} />
            <Route path="/diagnostics" element={<DiagnosticsHub />} />
            <Route path="/diagnostics/:slug" element={<DiagnosticDetail />} />
            <Route path="/faq" element={<FaqCenter />} />
            <Route path="/resources" element={<ResourcesHub />} />
            <Route path="/resources/:slug" element={<ResourceDetail />} />
            <Route path="/questions" element={<QuestionsHub />} />
            <Route path="/commercial-rfp" element={<CommercialRFP />} />

            {/* Q1 Duplicate-URL consolidation: redirect dupes to canonical pages */}
            <Route path="/log-home-inspection" element={<Navigate to="/log-home-health-inspection" replace />} />
            <Route path="/dock-restoration" element={<Navigate to="/dock-refinishing" replace />} />
            <Route path="/anti-graffiti-anti-microbial-coatings" element={<Navigate to="/anti-graffiti-coating" replace />} />
            <Route path="/intumescent-fireproof-coatings" element={<Navigate to="/intumescent-fireproofing" replace />} />

            <Route path="/exterior-painting" element={<ExteriorPainting />} />
            <Route path="/barn-painting" element={<BarnPainting />} />
            <Route path="/deck-staining" element={<DeckStaining />} />
            <Route path="/cabinet-painting" element={<CabinetPainting />} />
            <Route path="/commercial-painting" element={<CommercialPainting />} />
            <Route path="/fence-painting" element={<FencePainting />} />
            <Route path="/cabin-staining" element={<CabinStaining />} />
            <Route path="/cabin-restoration" element={<CabinRestoration />} />
            <Route path="/cabin-painting" element={<CabinPainting />} />
            
            {/* Main Pages */}
            <Route path="/about" element={<About />} />
            <Route path="/gallery" element={<Gallery />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/community" element={<Community />} />
            <Route path="/reviews" element={<Reviews />} />
            <Route path="/qr" element={<QRCodePage />} />
            <Route path="/certification" element={<Certification />} />
            <Route path="/apply" element={<Certification />} />
            <Route path="/best-painters-thumb-region-2026" element={<BestPaintersThumbRegion2026 />} />
            <Route path="/painting-cost-guide" element={<PaintingCostGuide />} />
            <Route path="/shake-roof-restoration" element={<ShakeRoofRestoration />} />
            <Route path="/wood-restoration" element={<WoodRestoration />} />
            <Route path="/cedar-siding-restoration" element={<CedarSidingRestoration />} />
            <Route path="/pergola-gazebo-restoration" element={<PergolaGazeboRestoration />} />
            <Route path="/window-door-frame-restoration" element={<WindowDoorFrameRestoration />} />
            <Route path="/soffit-fascia-restoration" element={<SoffitFasciaRestoration />} />
            <Route path="/interior-wood-paneling-restoration" element={<InteriorWoodPanelingRestoration />} />

            {/* Winter interior + competitor-gap services */}
            <Route path="/drywall-repair" element={<DrywallRepair />} />
            <Route path="/drywall-finishing" element={<DrywallFinishing />} />
            <Route path="/ceiling-painting" element={<CeilingPainting />} />
            <Route path="/trim-painting" element={<TrimPainting />} />
            <Route path="/pressure-washing" element={<PressureWashing />} />
            <Route path="/light-carpentry-paint-prep" element={<LightCarpentryPrep />} />

            {/* Marine Coatings */}
            <Route path="/marine-coatings" element={<MarineCoatings />} />
            <Route path="/boat-hull-painting" element={<BoatHullPainting />} />
            <Route path="/dock-steel-coatings" element={<DockSteelCoatings />} />
            <Route path="/boathouse-coatings" element={<BoathouseCoatings />} />
            <Route path="/seawall-coatings" element={<SeawallCoatings />} />
            <Route path="/pontoon-aluminum-refinishing" element={<PontoonAluminumRefinishing />} />
            <Route path="/marina-infrastructure-coatings" element={<MarinaInfrastructureCoatings />} />
            <Route path="/lighthouse-historic-marine-restoration" element={<LighthouseHistoricMarineRestoration />} />
            <Route path="/boat-trailer-davit-coatings" element={<BoatTrailerDavitCoatings />} />
            <Route path="/splash-zone-epoxy-coatings" element={<SplashZoneEpoxyCoatings />} />

            {/* Industrial Coatings */}
            <Route path="/industrial-coatings" element={<IndustrialCoatings />} />
            <Route path="/epoxy-floor-coatings" element={<EpoxyFloorCoatings />} />
            <Route path="/polyaspartic-floor-coatings" element={<PolyasparticFloorCoatings />} />
            <Route path="/grain-bin-silo-coatings" element={<GrainBinSiloCoatings />} />
            <Route path="/structural-steel-painting" element={<StructuralSteelPainting />} />
            <Route path="/tank-vessel-lining" element={<TankVesselLining />} />
            <Route path="/industrial-wall-ceiling-coatings" element={<IndustrialWallCeilingCoatings />} />
            <Route path="/heavy-equipment-refinishing" element={<Navigate to="/industrial-coatings" replace />} />
            <Route path="/concrete-sealers-polished" element={<ConcreteSealersPolished />} />
            <Route path="/warehouse-line-striping" element={<WarehouseLineStriping />} />
            <Route path="/intumescent-fireproof-coatings" element={<IntumescentFireproofCoatings />} />
            <Route path="/chemical-resistant-coatings" element={<ChemicalResistantCoatings />} />
            <Route path="/powder-coat-touchup-field-repair" element={<PowderCoatTouchupFieldRepair />} />
            <Route path="/anti-graffiti-anti-microbial-coatings" element={<AntiGraffitiAntiMicrobialCoatings />} />

            {/* Door Refinishing */}
            <Route path="/door-refinishing" element={<DoorRefinishing />} />
            <Route path="/front-door-refinishing" element={<FrontDoorRefinishing />} />
            <Route path="/entry-door-staining" element={<EntryDoorStaining />} />
            <Route path="/fiberglass-door-restoration" element={<FiberglassDoorRestoration />} />
            <Route path="/door-color-consulting" element={<DoorColorConsulting />} />

            {/* Log Home Restoration */}
            <Route path="/log-home" element={<LogHome />} />
            <Route path="/log-home-chinking" element={<LogHomeChinking />} />
            <Route path="/log-home-borate-treatment" element={<LogHomeBorateTreatment />} />
            <Route path="/log-home-media-blasting" element={<LogHomeMediaBlasting />} />
            <Route path="/log-home-superdeck-staining" element={<LogHomeSuperdeckStaining />} />
            <Route path="/log-home-inspection" element={<LogHomeInspection />} />
            <Route path="/log-home-maintenance-plan" element={<LogHomeMaintenancePlan />} />

            {/* Agricultural Coatings */}
            <Route path="/agricultural-coatings" element={<AgriculturalCoatings />} />
            <Route path="/pole-barn-repaint" element={<PoleBarnRepaint />} />
            <Route path="/silo-coating" element={<SiloCoating />} />
            <Route path="/grain-bin-repaint" element={<GrainBinRepaint />} />
            <Route path="/dairy-barn-sanitary-coating" element={<DairyBarnSanitaryCoating />} />
            <Route path="/equipment-shed-coating" element={<EquipmentShedCoating />} />

            {/* Residential Specialty */}
            <Route path="/brick-limewashing" element={<BrickLimewashing />} />
            <Route path="/german-smear-mortar-wash" element={<GermanSmearMortarWash />} />
            <Route path="/cedar-shake-restoration" element={<CedarShakeRestoration />} />
            <Route path="/deck-resurfacing" element={<DeckResurfacing />} />
            <Route path="/concrete-staining" element={<ConcreteStaining />} />
            <Route path="/epoxy-garage-floors" element={<EpoxyGarageFloors />} />
            <Route path="/historic-paint-restoration" element={<HistoricPaintRestoration />} />
            <Route path="/cabinet-refinishing" element={<CabinetRefinishing />} />
            <Route path="/garage-door-refinishing" element={<GarageDoorRefinishing />} />
            <Route path="/shutter-restoration" element={<ShutterRestoration />} />

            {/* Marine & Industrial (Sprint 6) */}
            <Route path="/marine-bottom-paint" element={<MarineBottomPaint />} />
            <Route path="/dock-refinishing" element={<DockRefinishing />} />
            <Route path="/boat-lift-coating" element={<BoatLiftCoating />} />
            <Route path="/industrial-tank-coating" element={<IndustrialTankCoating />} />
            <Route path="/anti-graffiti-coating" element={<AntiGraffitiCoating />} />
            <Route path="/intumescent-fireproofing" element={<IntumescentFireproofing />} />
            <Route path="/elastomeric-roof-coating" element={<ElastomericRoofCoating />} />

            {/* Industry Verticals (Sprint 7) */}
            <Route path="/painting-for-hotels-inns" element={<HotelInnPainting />} />
            <Route path="/hoa-painting" element={<HoaPainting />} />
            <Route path="/marina-painting" element={<MarinaPainting />} />
            <Route path="/winery-orchard-painting" element={<WineryOrchardPainting />} />
            <Route path="/healthcare-facility-painting" element={<HealthcareFacilityPainting />} />
            <Route path="/school-daycare-painting" element={<SchoolDaycarePainting />} />
            <Route path="/municipal-painting" element={<MunicipalPainting />} />
            <Route path="/self-storage-painting" element={<SelfStoragePainting />} />
            <Route path="/apartment-painting" element={<ApartmentPainting />} />
            <Route path="/restaurant-kitchen-painting" element={<RestaurantKitchenPainting />} />
            <Route path="/retail-storefront-painting" element={<RetailStorefrontPainting />} />
            <Route path="/auto-dealership-painting" element={<AutoDealershipPainting />} />
            <Route path="/funeral-home-painting" element={<FuneralHomePainting />} />
            <Route path="/church-painting" element={<ChurchPainting />} />
            <Route path="/museum-historic-painting" element={<MuseumHistoricPainting />} />

            {/* Moat Paid Diagnostic & Consulting Services (Sprint 7) */}
            <Route path="/paint-failure-diagnosis" element={<PaintFailureDiagnosis />} />
            <Route path="/log-home-health-inspection" element={<LogHomeHealthInspection />} />
            <Route path="/color-design-consultation" element={<ColorDesignConsultation />} />
            <Route path="/hoa-spec-writing" element={<HoaSpecWriting />} />
            <Route path="/historic-paint-analysis" element={<HistoricPaintAnalysis />} />

            {/* Membership Plans (Sprint 8 — 6 recurring care plans) */}
            <Route path="/residential-maintenance-plan" element={<ResidentialMaintenancePlan />} />
            <Route path="/log-home-care-plan" element={<LogHomeCarePlan />} />
            <Route path="/front-door-refresh-plan" element={<FrontDoorRefreshPlan />} />
            <Route path="/waterfront-protection-plan" element={<WaterfrontProtectionPlan />} />
            <Route path="/farm-coating-plan" element={<FarmCoatingPlan />} />
            <Route path="/commercial-maintenance-plan" element={<CommercialMaintenancePlan />} />

            {/* Customer Portal (public) */}
            <Route path="/portal/proposal/:token" element={<CustomerProposal />} />
            <Route path="/portal/estimate/:token" element={<CustomerEstimate />} />
            <Route path="/portal/invoice/:token" element={<CustomerInvoice />} />
            <Route path="/portal/change-order/:token" element={<ChangeOrderPortal />} />
            <Route path="/portal/survey/:token" element={<SurveyPortal />} />
            <Route path="/portal/warranty-claim/:token" element={<WarrantyClaimPortal />} />
            <Route path="/portal/job/:token" element={<JobTracker />} />
            <Route path="/portal/refer/:token" element={<ReferPortal />} />
            <Route path="/portal" element={<AccountHub />} />
            <Route path="/portal/warranties" element={<PortalWarranties />} />
            <Route path="/portal/photos" element={<PortalPhotos />} />
            <Route path="/portal/referrals" element={<PortalReferrals />} />
            <Route path="/auth" element={<CustomerAuth />} />
            <Route path="/refer/:token" element={<ReferPortal />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />
            <Route path="/review" element={<ReviewThanks />} />
            <Route path="/review/thanks" element={<ReviewThanks />} />
            <Route path="/review/:token" element={<ReviewRouter />} />
            <Route path="/review/:token/thanks" element={<ReviewThanks />} />
            <Route path="/feedback" element={<CustomerFeedback />} />
            <Route path="/feedback/:token" element={<CustomerFeedback />} />
            
            {/* Legal Pages */}
            <Route path="/privacy-policy" element={<PrivacyPage />} />
            <Route path="/terms-and-conditions" element={<TermsPage />} />
            {/* Aliases for older inbound links */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/sms-consent" element={<SmsConsentPage />} />

            {/* City Pages for SEO */}
            <Route path="/services" element={<ServicesIndex />} />
            <Route path="/services/:serviceSlug/:citySlug" element={<ServiceCityPageRoute />} />
            <Route path="/painters-bad-axe-mi" element={<PaintersBadAxeMi />} />
            <Route path="/painters-caro-mi" element={<PaintersCaroMi />} />
            <Route path="/painters-cass-city-mi" element={<PaintersCassCityMi />} />
            <Route path="/painters-sandusky-mi" element={<PaintersSanduskyMi />} />
            <Route path="/painters-harbor-beach-mi" element={<PaintersHarborBeachMi />} />
            <Route path="/painters-marlette-mi" element={<PainersMarletteMi />} />
            <Route path="/painters-sebewaing-mi" element={<PaintersSebewaingMi />} />
            <Route path="/painters-pigeon-mi" element={<PaintersPigeonMi />} />
            <Route path="/painters-ubly-mi" element={<PaintersUblyMi />} />
            <Route path="/painters-port-austin-mi" element={<PaintersPortAustinMi />} />
            <Route path="/painters-deckerville-mi" element={<PaintersDeckerville />} />
            <Route path="/painters-caseville-mi" element={<PaintersCasevilleMi />} />
            <Route path="/painters-sand-point-mi" element={<PaintersSandPointMi />} />
            <Route path="/painters-port-hope-mi" element={<PaintersPortHopeMi />} />
            <Route path="/painters-elkton-mi" element={<PaintersElktonMi />} />
            <Route path="/painters-kinde-mi" element={<PaintersKindeMi />} />
            <Route path="/painters-owendale-mi" element={<PaintersOwendaleMi />} />
            <Route path="/painters-vassar-mi" element={<PaintersVassarMi />} />
            <Route path="/painters-reese-mi" element={<PaintersReeseMi />} />
            <Route path="/painters-mayville-mi" element={<PaintersMayvilleMi />} />
            <Route path="/painters-millington-mi" element={<PaintersMillingtonMi />} />
            <Route path="/painters-akron-mi" element={<PaintersAkronMi />} />
            <Route path="/painters-unionville-mi" element={<PaintersUnionvilleMi />} />
            <Route path="/painters-fairgrove-mi" element={<PaintersFairgroveMi />} />
            <Route path="/painters-kingston-mi" element={<PaintersKingstonMi />} />
            <Route path="/painters-croswell-mi" element={<PaintersCroswellMi />} />
            <Route path="/painters-carsonville-mi" element={<PaintersCarsonvilleMi />} />
            <Route path="/painters-peck-mi" element={<PaintersPeckMi />} />
            <Route path="/painters-brown-city-mi" element={<PaintersBrownCityMi />} />
            <Route path="/painters-applegate-mi" element={<PaintersApplegateMi />} />

            {/* Q3 Secondary-market expansion cities */}
            <Route path="/painters-frankenmuth-mi" element={<PaintersFrankenmuthMi />} />
            <Route path="/painters-birch-run-mi" element={<PaintersBirchRunMi />} />
            <Route path="/painters-lexington-mi" element={<PaintersLexingtonMi />} />
            <Route path="/painters-marine-city-mi" element={<PaintersMarineCityMi />} />
            <Route path="/painters-port-huron-mi" element={<PaintersPortHuronMi />} />
            <Route path="/painters-bay-city-mi" element={<PaintersBayCityMi />} />
            <Route path="/painters-saginaw-mi" element={<PaintersSaginawMi />} />
            <Route path="/painters-midland-mi" element={<PaintersMidlandMi />} />
            <Route path="/painters-algonac-mi" element={<PaintersAlgonacMi />} />

            {/* Admin Auth (public) */}
            <Route path="/admin/auth" element={<AdminAuth />} />
            <Route path="/admin/auth/welcome" element={<AdminAuthWelcome />} />
            <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />

            {/* Admin Protected Routes */}
            <Route path="/admin" element={<RoleRoute allow={["admin"]}><AdminDashboard /></RoleRoute>} />
            <Route path="/admin/inbox" element={<RoleRoute allow={["admin"]}><AdminInbox /></RoleRoute>} />
            <Route path="/admin/leads" element={<RoleRoute allow={["admin"]}><AdminLeads /></RoleRoute>} />
            <Route path="/admin/leads/:id" element={<RoleRoute allow={["admin"]}><AdminLeadWorkspace /></RoleRoute>} />
            <Route path="/admin/intake" element={<RoleRoute allow={["admin"]}><AdminIntake /></RoleRoute>} />
            <Route path="/admin/saturation" element={<RoleRoute allow={["admin"]}><AdminSaturation /></RoleRoute>} />
            <Route path="/admin/coverage-map" element={<RoleRoute allow={["admin"]}><AdminCoverageMap /></RoleRoute>} />
            <Route path="/admin/expansion-roadmap" element={<RoleRoute allow={["admin"]}><AdminExpansionRoadmap /></RoleRoute>} />
            <Route path="/admin/jobs" element={<RoleRoute allow={["admin","foreman"]}><AdminSectionErrorBoundary label="jobs"><AdminJobs /></AdminSectionErrorBoundary></RoleRoute>} />
            <Route path="/admin/profit-loss" element={<RoleRoute allow={["admin","foreman"]}><AdminSectionErrorBoundary label="profit-loss"><AdminJobProfitLoss /></AdminSectionErrorBoundary></RoleRoute>} />
            <Route path="/admin/crew-updates" element={<RoleRoute allow={["admin","foreman"]}><AdminCrewUpdates /></RoleRoute>} />
            <Route path="/admin/crew-app-issues" element={<RoleRoute allow={["admin"]}><AdminCrewAppIssues /></RoleRoute>} />
            <Route path="/admin/executive" element={<RoleRoute allow={["admin"]}><AdminExecutiveDashboard /></RoleRoute>} />
            <Route path="/admin/recovery" element={<RoleRoute allow={["admin"]}><AdminRevenueRecovery /></RoleRoute>} />
            <Route path="/admin/intelligence" element={<RoleRoute allow={["admin"]}><AdminIntelligenceHub /></RoleRoute>} />
            <Route path="/admin/business-brain" element={<RoleRoute allow={["admin"]}><AdminBusinessBrain /></RoleRoute>} />
            <Route path="/admin/estimating-intelligence" element={<RoleRoute allow={["admin"]}><AdminEstimatingIntelligence /></RoleRoute>} />
            <Route path="/admin/marketing-profitability" element={<RoleRoute allow={["admin"]}><AdminMarketingProfitability /></RoleRoute>} />
            <Route path="/admin/maintenance-intelligence" element={<RoleRoute allow={["admin"]}><AdminMaintenanceIntelligence /></RoleRoute>} />
            <Route path="/admin/autonomous-operations" element={<RoleRoute allow={["admin"]}><AdminAutonomousOperations /></RoleRoute>} />
            <Route path="/admin/searchatlas-intelligence" element={<RoleRoute allow={["admin"]}><AdminSearchAtlasIntelligence /></RoleRoute>} />
            <Route path="/admin/content-domination" element={<RoleRoute allow={["admin"]}><AdminContentDomination /></RoleRoute>} />
           <Route path="/admin/authority" element={<RoleRoute allow={["admin"]}><AdminAuthorityDashboard /></RoleRoute>} />
            <Route path="/admin/estimates" element={<RoleRoute allow={["admin"]}><AdminEstimates /></RoleRoute>} />
            <Route path="/admin/invoices" element={<RoleRoute allow={["admin"]}><AdminInvoices /></RoleRoute>} />
            <Route path="/admin/payments" element={<RoleRoute allow={["admin"]}><AdminPayments /></RoleRoute>} />
            <Route path="/admin/referrals" element={<RoleRoute allow={["admin"]}><AdminReferrals /></RoleRoute>} />
            <Route path="/admin/follow-ups" element={<RoleRoute allow={["admin"]}><AdminFollowUps /></RoleRoute>} />
            <Route path="/admin/reports/profit" element={<RoleRoute allow={["admin"]}><AdminProfitDashboard /></RoleRoute>} />
            <Route path="/admin/calendar" element={<RoleRoute allow={["admin","foreman"]}><AdminCalendar /></RoleRoute>} />
            <Route path="/admin/media" element={<RoleRoute allow={["admin"]}><AdminMediaGallery /></RoleRoute>} />
            <Route path="/admin/email-marketing" element={<RoleRoute allow={["admin"]}><AdminEmailMarketing /></RoleRoute>} />
            <Route path="/admin/email-health" element={<RoleRoute allow={["admin"]}><AdminEmailHealth /></RoleRoute>} />
            <Route path="/admin/reports" element={<RoleRoute allow={["admin"]}><AdminReports /></RoleRoute>} />
            <Route path="/admin/tasks" element={<RoleRoute allow={["admin","foreman","worker"]}><AdminSectionErrorBoundary label="tasks"><AdminTasks /></AdminSectionErrorBoundary></RoleRoute>} />
            <Route path="/admin/crews" element={<RoleRoute allow={["admin"]}><AdminCrews /></RoleRoute>} />
            <Route path="/admin/team" element={<RoleRoute allow={["admin"]}><AdminTeam /></RoleRoute>} />
            <Route path="/admin/sms" element={<RoleRoute allow={["admin"]}><AdminSms /></RoleRoute>} />
            <Route path="/admin/thank-you-justin" element={<RoleRoute allow={["admin"]}><ThankYouEmailJustin /></RoleRoute>} />
            <Route path="/admin/settings" element={<RoleRoute allow={["admin"]}><AdminSettings /></RoleRoute>} />
            <Route path="/admin/qa" element={<RoleRoute allow={["admin"]}><QaConsole /></RoleRoute>} />
            <Route path="/admin/flyer" element={<RoleRoute allow={["admin"]}><AdminFlyer /></RoleRoute>} />
            <Route path="/admin/blog" element={<RoleRoute allow={["admin"]}><AdminBlogPosts /></RoleRoute>} />
            <Route path="/admin/reviews" element={<RoleRoute allow={["admin"]}><AdminReviews /></RoleRoute>} />
            <Route path="/admin/projects" element={<RoleRoute allow={["admin"]}><AdminProjects /></RoleRoute>} />
            <Route path="/admin/projects/:id" element={<RoleRoute allow={["admin"]}><AdminProjectEditor /></RoleRoute>} />
            <Route path="/admin/home-pairs" element={<RoleRoute allow={["admin"]}><AdminHomeFeatured /></RoleRoute>} />
            <Route path="/admin/gbp" element={<RoleRoute allow={["admin"]}><AdminGbpComposer /></RoleRoute>} />
            <Route path="/admin/authority-program" element={<RoleRoute allow={["admin"]}><AdminAuthorityProgram /></RoleRoute>} />
            <Route path="/admin/content" element={<RoleRoute allow={["admin"]}><AdminContentEditor /></RoleRoute>} />
            <Route path="/admin/llm-visibility" element={<RoleRoute allow={["admin"]}><AdminLlmVisibility /></RoleRoute>} />
            <Route path="/admin/blog-engine" element={<RoleRoute allow={["admin"]}><AdminBlogEngine /></RoleRoute>} />
            <Route path="/admin/trophy-listicle" element={<RoleRoute allow={["admin"]}><AdminTrophyListicle /></RoleRoute>} />
            <Route path="/admin/h2h-comparison" element={<RoleRoute allow={["admin"]}><AdminH2HComparison /></RoleRoute>} />
            <Route path="/admin/topical-authority" element={<RoleRoute allow={["admin"]}><AdminTopicalAuthority /></RoleRoute>} />
            <Route path="/admin/foundation-authority" element={<RoleRoute allow={["admin"]}><AdminFoundationAuthority /></RoleRoute>} />
            <Route path="/admin/authority-acceleration" element={<RoleRoute allow={["admin"]}><AdminAuthorityAcceleration /></RoleRoute>} />
            <Route path="/admin/autonomous-authority" element={<RoleRoute allow={["admin"]}><AdminAutonomousAuthority /></RoleRoute>} />
            <Route path="/admin/outreach-campaign" element={<RoleRoute allow={["admin"]}><AdminOutreachCampaign /></RoleRoute>} />
            <Route path="/admin/cloud-stack-trust" element={<RoleRoute allow={["admin"]}><AdminCloudStackTrust /></RoleRoute>} />
            <Route path="/admin/journalist-outreach" element={<RoleRoute allow={["admin"]}><AdminJournalistOutreach /></RoleRoute>} />
            <Route path="/admin/press-release-authority" element={<RoleRoute allow={["admin"]}><AdminPressReleaseAuthority /></RoleRoute>} />
            <Route path="/admin/keyword-publisher-outreach" element={<RoleRoute allow={["admin"]}><AdminKeywordPublisherOutreach /></RoleRoute>} />
            <Route path="/admin/seo-content-strategy" element={<RoleRoute allow={["admin"]}><AdminSeoContentStrategy /></RoleRoute>} />
            <Route path="/admin/entity-graph" element={<RoleRoute allow={["admin"]}><AdminEntityGraph /></RoleRoute>} />
            <Route path="/admin/local-seo-geo-grid" element={<RoleRoute allow={["admin"]}><AdminLocalSeoGeoGrid /></RoleRoute>} />
            <Route path="/admin/campaign-cockpit" element={<RoleRoute allow={["admin"]}><AdminCampaignCockpit /></RoleRoute>} />
            <Route path="/admin/agent-performance" element={<RoleRoute allow={["admin"]}><AdminAgentPerformance /></RoleRoute>} />
            <Route path="/admin/blog-ideas" element={<RoleRoute allow={["admin"]}><AdminBlogIdeas /></RoleRoute>} />
            <Route path="/admin/time-clock" element={<RoleRoute allow={["admin","foreman","worker"]}><AdminSectionErrorBoundary label="time-clock"><AdminTimeClock /></AdminSectionErrorBoundary></RoleRoute>} />
            <Route path="/admin/timesheets" element={<RoleRoute allow={["admin"]}><AdminTimesheets /></RoleRoute>} />
            <Route path="/admin/expenses" element={<RoleRoute allow={["admin","foreman","worker"]}><AdminSectionErrorBoundary label="expenses"><AdminExpenses /></AdminSectionErrorBoundary></RoleRoute>} />
            <Route path="/admin/my-pay" element={<RoleRoute allow={["admin","foreman","worker"]}><AdminSectionErrorBoundary label="my-pay"><AdminMyPay /></AdminSectionErrorBoundary></RoleRoute>} />
            <Route path="/admin/home" element={<RoleRoute allow={["admin","foreman","worker"]}><AdminSectionErrorBoundary label="crew-home"><CrewHome /></AdminSectionErrorBoundary></RoleRoute>} />
            <Route path="/admin/certifications" element={<RoleRoute allow={["admin"]}><AdminCertifications /></RoleRoute>} />
            <Route path="/admin/certifications/questions" element={<RoleRoute allow={["admin"]}><AdminCertificationQuestions /></RoleRoute>} />
            <Route path="/admin/certifications/photos" element={<RoleRoute allow={["admin"]}><AdminCertificationPhotos /></RoleRoute>} />
            <Route path="/admin/certifications/:id" element={<RoleRoute allow={["admin"]}><AdminCertificationApplicant /></RoleRoute>} />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
          </ImpersonationProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);
};
export default App;
